/**
 * Turn an address into something safe to print in a log line.
 *
 * Some of the addresses this app handles carry a credential in their query string. The Discourse
 * community login is the one that prompted this (#4525): its address carries a base64 payload
 * naming the account plus `sig`, an HMAC over that payload computed with the secret the forum and
 * we both hold — so the address is a working login for that person's forum account. A Google Cloud
 * Storage signed upload URL is the same idea with a different parameter.
 *
 * Printing one matters more here than it would elsewhere, because `LoggingService` replaces every
 * `console` function and turns each call into a Sentry breadcrumb, and the E2E harness streams the
 * browser console into a file CI publishes as a build artifact. So a console line is not a local
 * thing — it leaves the device.
 *
 * The rule is deliberately blunt: keep the part that says WHERE, drop every part that could carry a
 * credential. Scheme, host and path survive; the query string and the fragment always go, whether
 * or not they look dangerous. Deciding case by case which query strings are safe is exactly the
 * judgement that keeps being got wrong, and getting it wrong leaks a credential.
 *
 * This module holds no Angular and no Sentry import, so it stays unit-testable — same reasoning as
 * `breadcrumb-redaction.ts`, which solves the neighbouring problem of a credential that reaches
 * Sentry without any log line being involved.
 */

/**
 * What is printed instead of an address that could not be taken apart.
 *
 * Says the address was unreadable rather than that there was none, so nobody debugging concludes
 * the caller was handed nothing. Printing the string itself is not an option: if we cannot find
 * where its query string starts, we cannot know a credential is not inside it.
 */
const UNREADABLE_ADDRESS_MARKER = '<unreadable address>';

/**
 * The address with its query string and fragment removed, or a marker when it cannot be read.
 *
 * Built from the protocol, host and path rather than from `URL.origin`, because `origin` is the
 * string `'null'` for a scheme the URL standard does not call special — and `capacitor://localhost`
 * is exactly that, which is how our own pages are served on the phone apps.
 */
export function safeUrlForLog(url: string): string {
  // if we were not given text at all, there is nothing to take apart
  if (typeof url !== 'string') {
    return UNREADABLE_ADDRESS_MARKER;
  }
  try {
    const parsed = new URL(url);
    return `${parsed.protocol}//${parsed.host}${parsed.pathname}`;
  } catch {
    // URL() throws on anything it cannot parse, including an empty string
    return UNREADABLE_ADDRESS_MARKER;
  }
}
